/**
 * useOrderActions wrap service that mutate order delivery information like 'mark as packaged'
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import useIonicToast from '../../../hooks/useIonicToast';
import { markOrderAsPackaged } from '../markOrderAsPackaged';
import { updateOrderStatus } from '../updateOrderStatus';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import { useDialog } from '../../../context/Dialog';
import ActionConfirmationDialog from '../../../components/ui/ActionConfirmationDialog';

export default function useOrderActions() {
  const { presentIonToast } = useIonicToast();
  const { openDialog, closeDialog, setBusy } = useDialog();
  const queryClient = useQueryClient();
  const invalidateOrdersCache = () =>
    queryClient.invalidateQueries({ queryKey: ['orders'], exact: false });
  const globalState = useSelector((state: RootState) => ({
    authToken: state.userSession.signedAccessToken,
    storeId: state.userStores.activeStore?.id,
  }));

  const markAsPackedMechanics = useMutation({
    mutationFn: async (orderReference: string) => {
      if (!globalState.authToken || !globalState.storeId) return;
      const res = await markOrderAsPackaged({
        authToken: globalState.authToken,
        storeId: globalState.storeId,
        reference: orderReference,
      });
      console.log(res);
    },
    onError: (err: any) => presentIonToast(err.message, 'error'),
    onSuccess: () => {
      invalidateOrdersCache();
      presentIonToast('Order has been marked for delivery!');
    },
  });

  const markAsPacked = async (orderReference: string, orderTitle: string) =>
    openDialog(
      <ActionConfirmationDialog
        message={`Are you sure order for "${orderTitle}" as been packed?`}
        title="Mark as packed"
        confirmationType="default"
        onConfirm={async () => {
          try {
            setBusy(true);
            await markAsPackedMechanics.mutateAsync(orderReference);
          } catch (error) {
            console.log(error);
          } finally {
            setBusy(false);
            closeDialog({ keepParent: true });
          }
        }}
        onCancel={() => closeDialog({ keepParent: true })}
      />,
      { allowBackgroundDismiss: true }
    );

  const updateStatusMutation = useMutation({
    mutationFn: async ({
      reference,
      status,
    }: {
      reference: string;
      status: 'shipped' | 'delivered' | 'cancelled';
    }) => {
      if (!globalState.authToken || !globalState.storeId) return;
      return updateOrderStatus({
        authToken: globalState.authToken,
        storeId: globalState.storeId,
        reference,
        status,
      });
    },
    onError: (err: any) => presentIonToast(err.message, 'error'),
    onSuccess: (_data, variables) => {
      invalidateOrdersCache();
      const labels: Record<string, string> = {
        shipped: 'marked as shipped',
        delivered: 'marked as delivered',
        cancelled: 'cancelled',
      };
      presentIonToast(`Order has been ${labels[variables.status]}!`);
    },
  });

  const markAsShipped = async (orderReference: string, orderTitle: string) =>
    openDialog(
      <ActionConfirmationDialog
        message={`Confirm that order for "${orderTitle}" has been shipped?`}
        title="Mark as Shipped"
        confirmationType="default"
        onConfirm={async () => {
          try {
            setBusy(true);
            await updateStatusMutation.mutateAsync({
              reference: orderReference,
              status: 'shipped',
            });
          } catch (error) {
            console.log(error);
          } finally {
            setBusy(false);
            closeDialog({ keepParent: true });
          }
        }}
        onCancel={() => closeDialog({ keepParent: true })}
      />,
      { allowBackgroundDismiss: true }
    );

  const markAsDelivered = async (orderReference: string, orderTitle: string) =>
    openDialog(
      <ActionConfirmationDialog
        message={`Confirm that order for "${orderTitle}" has been delivered?`}
        title="Mark as Delivered"
        confirmationType="default"
        onConfirm={async () => {
          try {
            setBusy(true);
            await updateStatusMutation.mutateAsync({
              reference: orderReference,
              status: 'delivered',
            });
          } catch (error) {
            console.log(error);
          } finally {
            setBusy(false);
            closeDialog({ keepParent: true });
          }
        }}
        onCancel={() => closeDialog({ keepParent: true })}
      />,
      { allowBackgroundDismiss: true }
    );

  return {
    markAsPacked,
    markAsShipped,
    markAsDelivered,
  };
}
